import { type FC, useEffect, useRef, useState } from "react"
import DatePicker, { type DatePickerProps } from "react-date-picker"
import { type Control, Controller, type FieldValues } from "react-hook-form"
import "react-date-picker/dist/DatePicker.css"
import "react-calendar/dist/Calendar.css"

type ExtraDatePickerProps = Omit<
  DatePickerProps,
  "className" | "onBlur" | "onChange" | "value" | "calendarProps"
>

export const DatePickerField: FC<{
  name: string
  placeholder: string
  error: string | undefined
  control: Control<FieldValues>
  additionalProps?: ExtraDatePickerProps
  className?: string
  errorInFlow?: boolean
}> = ({
  name,
  error,
  control,
  placeholder = "YYYY-MM-DD",
  additionalProps = {},
  className = "",
  errorInFlow = false,
}) => {
  const [isFocusedWithin, setIsFocusedWithin] = useState(false)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const fieldRef = useRef<HTMLDivElement | null>(null)
  const blurFrameRef = useRef<number | null>(null)
  const lastCalendarInteractionAtRef = useRef(0)
  // Safari (desktop + iOS) doesn't transfer focus when clicking non-text elements
  // like calendar tiles (WebKit bug #22261, WONTFIX). This causes react-date-picker
  // to misfire blur/focus events, reopening the calendar after selection or
  // closing it during year/month navigation. We use controlled isOpen state
  // and interaction timestamps to work around this.
  const isSafari =
    typeof navigator !== "undefined" &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) ||
      (/Safari/.test(navigator.userAgent) && !/Chrome|CriOS|FxiOS|Edg/.test(navigator.userAgent)))
  const isTouchDevice =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches
  const usesControlledCalendar = isSafari || isTouchDevice
  const shouldSuppressSoftKeyboard = isTouchDevice

  const isCalendarTarget = (target: EventTarget | null) =>
    (target as HTMLElement | null)?.closest(".react-date-picker__calendar")

  const isDateInputTarget = (target: EventTarget | null) =>
    (target as HTMLElement | null)?.closest(".react-date-picker__inputGroup")

  const openCalendarFromTouchFieldTap = (target: EventTarget | null) => {
    if (!usesControlledCalendar || !isDateInputTarget(target)) {
      return false
    }

    if (blurFrameRef.current !== null) {
      cancelAnimationFrame(blurFrameRef.current)
      blurFrameRef.current = null
    }

    setIsFocusedWithin(true)
    setIsCalendarOpen(true)
    return true
  }

  useEffect(() => {
    return () => {
      if (blurFrameRef.current !== null) {
        cancelAnimationFrame(blurFrameRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const field = fieldRef.current
    if (!field) return

    field
      .querySelectorAll<HTMLInputElement>(".react-date-picker__inputGroup__input")
      .forEach((input) => {
        if (shouldSuppressSoftKeyboard) {
          input.readOnly = true
          input.inputMode = "none"
          input.setAttribute("aria-readonly", "true")
        } else {
          input.readOnly = false
          input.inputMode = "numeric"
          input.removeAttribute("aria-readonly")
        }
      })
  }, [shouldSuppressSoftKeyboard])

  return (
    <div className="relative w-full">
      <div className="relative z-50" ref={fieldRef}>
        <Controller
          control={control}
          name={name}
          render={({ field: { value, onChange } }) => (
            <div
              className={`h-12 px-4 border w-full rounded-sm ${className} ${
                error
                  ? "border-[#FF1C5E]"
                  : "border-[#232734] border-opacity-50 focus-within:border-opacity-100"
              }`}
            >
              <div
                className="relative flex h-full w-full items-center"
                onBlurCapture={(event) => {
                  const currentTarget = event.currentTarget

                  if (blurFrameRef.current !== null) {
                    cancelAnimationFrame(blurFrameRef.current)
                  }

                  blurFrameRef.current = requestAnimationFrame(() => {
                    const isRecentCalendarInteraction =
                      Date.now() - lastCalendarInteractionAtRef.current < 500

                    if (isSafari && isRecentCalendarInteraction) {
                      return
                    }

                    if (!currentTarget.contains(document.activeElement)) {
                      setIsFocusedWithin(false)
                      if (usesControlledCalendar) {
                        setIsCalendarOpen(false)
                      }
                    }
                  })
                }}
                onFocusCapture={() => {
                  if (blurFrameRef.current !== null) {
                    cancelAnimationFrame(blurFrameRef.current)
                    blurFrameRef.current = null
                  }
                  setIsFocusedWithin(true)
                  if (usesControlledCalendar) {
                    setIsCalendarOpen(true)
                  }
                }}
                onMouseDownCapture={(event) => {
                  if (isCalendarTarget(event.target)) {
                    lastCalendarInteractionAtRef.current = Date.now()
                  }
                }}
                onTouchStartCapture={(event) => {
                  if (isCalendarTarget(event.target)) {
                    lastCalendarInteractionAtRef.current = Date.now()
                    return
                  }

                  // On touch devices, tapping the segmented date inputs focuses them first,
                  // which opens the numeric keyboard before the calendar. Intercept the
                  // tap so the shared date picker opens the calendar directly instead.
                  if (openCalendarFromTouchFieldTap(event.target)) {
                    event.preventDefault()
                  }
                }}
              >
                {!value && !isFocusedWithin && (
                  <span className="absolute left-0 pointer-events-none text-body-lg text-[#232734] opacity-50">
                    {placeholder}
                  </span>
                )}
                <DatePicker
                  {...additionalProps}
                  calendarIcon={null}
                  calendarProps={{
                    defaultActiveStartDate: new Date(1995, 0, 1),
                    defaultView: "decade",
                    onClickDay: () => {
                      if (usesControlledCalendar) {
                        setIsCalendarOpen(false)
                      }
                    },
                  }}
                  className={`w-full h-full bg-transparent date-picker-custom text-body-lg ${!value && !isFocusedWithin ? "date-picker-empty" : ""}`}
                  clearIcon={null}
                  dayPlaceholder="DD"
                  format="yyyy-MM-dd"
                  isOpen={usesControlledCalendar ? isCalendarOpen : undefined}
                  maxDate={new Date()}
                  minDate={new Date(1900, 0, 1)}
                  monthPlaceholder="MM"
                  onBlur={() => {}}
                  onCalendarClose={() => {
                    setIsCalendarOpen(false)
                  }}
                  onCalendarOpen={() => {
                    setIsCalendarOpen(true)
                  }}
                  onChange={(date) => onChange(date)}
                  onClick={() => {
                    if (usesControlledCalendar) {
                      setIsCalendarOpen(true)
                    }
                  }}
                  openCalendarOnFocus={!usesControlledCalendar}
                  shouldCloseCalendar={({ reason }) => {
                    // In Safari, clicking calendar tiles (year/month navigation) fires
                    // "outsideAction" because focus doesn't move to the clicked element.
                    // Use the interaction timestamp to distinguish real outside clicks
                    // from false positives caused by Safari's focus behavior.
                    if (reason === "outsideAction" && isSafari) {
                      return Date.now() - lastCalendarInteractionAtRef.current > 500
                    }
                    return true
                  }}
                  shouldOpenCalendar={({ reason }) => !usesControlledCalendar || reason !== "focus"}
                  value={value}
                  yearPlaceholder="YYYY"
                />
              </div>
              <style global jsx>{`
                .date-picker-custom .react-date-picker__wrapper {
                  border: none !important;
                  background: transparent !important;
                  height: 100% !important;
                  width: 100% !important;
                }
                .date-picker-custom .react-date-picker__inputGroup {
                  height: 100% !important;
                  padding: 0 !important;
                }
                .date-picker-custom .react-date-picker__inputGroup__input {
                  font-size: 14px !important;
                  line-height: 125% !important;
                  font-weight: 400 !important;
                  font-family: var(--font-roboto) !important;
                  color: #232734 !important;
                  background: transparent !important;
                  border: none !important;
                  outline: none !important;
                  box-shadow: none !important;
                  height: 100% !important;
                }
                .date-picker-custom .react-date-picker__inputGroup__input:focus {
                  outline: none !important;
                  box-shadow: none !important;
                }
                .date-picker-custom .react-date-picker__inputGroup__input::placeholder {
                  color: rgba(35, 39, 52, 0.5) !important;
                }
                .date-picker-empty .react-date-picker__inputGroup {
                  opacity: 0 !important;
                }
                .date-picker-custom .react-date-picker__inputGroup__input:invalid {
                  color: #232734 !important;
                }
                .date-picker-custom .react-date-picker__inputGroup__divider {
                  color: #232734 !important;
                  font-size: 14px !important;
                  line-height: 125% !important;
                  font-weight: 400 !important;
                  font-family: var(--font-roboto) !important;
                }
                .react-calendar {
                  font-family: var(--font-roboto) !important;
                  font-size: 14px !important;
                  border-radius: 4px !important;
                  border-color: #232734 !important;
                  position: absolute !important;
                  top: 100% !important;
                  left: 0 !important;
                  z-index: 1000 !important;
                  margin-top: 4px !important;
                  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
                  background: white !important;
                }
                .react-calendar__tile {
                  font-size: 14px !important;
                  line-height: 125% !important;
                  font-weight: 400 !important;
                  font-family: var(--font-roboto) !important;
                  padding: 8px !important;
                }
                .react-calendar__navigation button {
                  font-size: 14px !important;
                  line-height: 125% !important;
                  font-weight: 400 !important;
                  font-family: var(--font-roboto) !important;
                }
                .react-calendar__month-view__weekdays__weekday {
                  font-size: 12px !important;
                  line-height: 125% !important;
                  font-weight: 500 !important;
                  font-family: var(--font-roboto) !important;
                }
              `}</style>
            </div>
          )}
        />
      </div>
      {!!error && (
        <span
          className={`first-letter:uppercase text-body-sm text-error ${
            errorInFlow ? "mt-1 block" : "absolute left-0 top-full"
          }`}
        >
          {error}
        </span>
      )}
    </div>
  )
}
